<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <b-table
      :id="id"
      ref="table"
      category="LineItems"
      :allow-selection="allowSelection"
      :expand="[
        'package',
        'campaign.advertiser.unique_advertiser.replacement_unique_advertiser.advertiser_domains',
        'cachedpacing',
        'lineitem_properties',
        'lineitem_networkfreqcaps',
      ]"
      :items="lineItems"
      :columns="columns"
      :show-filters="showFilters"
      :fixed-filters="internalFixedFilters"
      :total-count="lineItemsTotalCount"
      :is-loading="isLoadingLineItems"
      :enable-download="enableDownload"
      :table-download-columns="tableDownloadColumns"
      :enable-search-section="enableSearchSection"
      :text-search-properties="[
        'lineitem_id',
        'name',
        'campaign_id',
        'campaign.name',
        'campaign.advertiser.name',
        'campaign.account.name',
        'lineitem_properties.value',
      ]"
      pagination
      :cast-shadow="castShadow"
      :sort-column="columns.startDateFormatted"
      :sort-ascending="false"
      :is-main-table="isMainTable"
      :get-action="getAction"
      :is-editable="true"
      :editable-class="LineItem"
      :maximum-bulk-update-record-count="500"
      :event-category="eventCategory"
      :enable-views="enableViews"
      @loading-selection="() => $emit('loading-selection')"
      @filters-changed="filtersChanged"
      @download="onDownload"
      @selection-changed="onSelectionChanged">
      <template #extra-toolbar>
        <BulkCreation
          :event-category="eventCategory"
          :editable-class="LineItem" />
      </template>
      <template #actions>
        <slot name="actions" />
      </template>
      <template #link="{ item: lineItem }">
        <missing-creative-size-alert-icon
          v-if="lineItem.isMissingCreativeSizes"
          :missing-creative-sizes="lineItem.missingCreativeSizes" />
        <router-link
          :to="lineItem.endpoint"
          class="entity-link"
          :title="lineItem.name">
          <span
            v-if="lineItem.didPlayInTheLast12Hours"
            v-tooltip.top="capitalize($t('messages.lineItemIsRunningFine'))"
            class="icon success">
            <icon-good-filled />
          </span>
          <span
            v-else-if="lineItem.didNotPlayInTheLast24Hours"
            v-tooltip.top="capitalize($t('messages.lineItemHasNotPlayedInTheLast24Hours'))"
            class="icon danger">
            <icon-error-filled />
          </span>
          <span
            v-else-if="lineItem.didNotPlayInTheLast12Hours"
            v-tooltip.top="capitalize($t('messages.lineItemHasNotPlayedInTheLast12Hours'))"
            class="icon warning-dark">
            <icon-warning-filled />
          </span>
          <span
            v-if="!!lineItem.flightRatio"
            class="icon">
            <flight-alert-icon :name="lineItem.name" />
          </span>
          {{ lineItem.name }}
        </router-link>
      </template>
      <template #channelType="{ item: lineItem }">
        <span>{{ lineItem.channelTypeFormatted }}</span>
      </template>
      <template #statusDescription="{ item: lineItem }">
        <line-item-status-icon :status="lineItem.status" />
        <span v-text="capitalize($t(`states.${lineItem.status}`))" />
        <span
          v-if="lineItem.status === 'active' && lineItem.hasRejections"
          v-tooltip.top="capitalize($t('messages.lineItemHasBeenRejectedBySomeMediaOwners'))"
          class="has-rejections">
          <icon-warning
        /></span>
      </template>
      <template #supplySideCampaignName="{ item: lineItem }">
        <router-link
          v-if="lineItem.creationOrigin != 'dsp' && lineItem.creationOrigin != 'auto'"
          :to="lineItem.campaign.endpoint"
          class="entity-link"
          :title="lineItem.supplySideCampaignName">
          {{ lineItem.supplySideCampaignName }}
        </router-link>
        <span v-else>{{ lineItem.supplySideCampaignName }}</span>
      </template>
      <template #status="{ item: lineItem }">
        <line-item-status-icon :status="lineItemm.status" />
        {{ capitalize($t(`states.${lineItem.status}`)) }}
      </template>
      <template #advertiserDescription="{ item: lineItem }">
        <img
          v-if="lineItem.advertiserLogo != null"
          v-tooltip.top="lineItem.advertiserName"
          :src="lineItem.advertiserLogo"
          :alt="lineItem.advertiserName"
          class="advertiser-logo-img" />
        <span v-else>{{ lineItem.advertiserName }}</span>
        <i
          v-if="lineItem.advertiserDomain != null"
          class="advertiser-domain"
          >({{ lineItem.advertiserDomain }})</i
        >
      </template>
      <template #progressFormatted="{ item: lineItem }">
        <div class="progress-cell">
          {{ lineItem.progressFormatted }}
          <div
            v-if="lineItem.progressFormatted !== DASH"
            class="progress-container"
            :class="lineItem.hasStarted ? 'normal' : ''">
            <div
              class="progress"
              :style="`width: ${lineItem.progressFormatted}`" />
          </div>
        </div>
      </template>
      <template #pacingFormatted="{ item: lineItem }">
        <div class="progress-cell">
          <span
            v-if="lineItem.pacingFormatted !== DASH"
            class="badge"
            :class="lineItem.pacingColorIndicatorClassName">
            {{ lineItem.pacingFormatted }}
          </span>
          <span v-else>{{ DASH }}</span>
        </div>
      </template>
      <template #pacingVsEvenFormatted="{ item: lineItem }">
        <div class="progress-cell">
          <span
            v-if="lineItem.pacingVsEvenFormatted !== DASH"
            class="badge"
            :class="lineItem.pacingVsEvenColorIndicatorClassName">
            {{ lineItem.pacingVsEvenFormatted }}
          </span>
          <span v-else>{{ DASH }}</span>
        </div>
      </template>
      <template #mediaOwnerIDs="{ item: lineItem }">
        <MediaOwners :ids="lineItem.mediaOwnerIDs" />
      </template>
      <template #accountName="{ item: lineItem }">
        <img
          v-if="lineItem.accountLogoUrl != null"
          v-tooltip.top="lineItem.accountName"
          :src="lineItem.accountLogoUrl"
          :alt="lineItem.accountName"
          class="advertiser-logo-img" />
        <span v-else>{{ lineItem.accountName }}</span>
      </template>
      <template #lineItemProperties="{ item: lineItem }">
        <template
          v-if="
            organizationLineItemCustomFields != null &&
            lineItem.lineItemProperties.filter(
              (x) => organizationLineItemCustomFields.find((y) => y.key === x.key) != null,
            ).length > 0
          ">
          <span
            v-for="prop in lineItem.lineItemProperties.filter(
              (x) => organizationLineItemCustomFields.find((y) => y.key === x.key) != null,
            )"
            :key="prop.key"
            class="badge badge-info-hollow mr-3">
            <b
              >{{
                organizationLineItemCustomFields.find((x) => x.key === prop.key) != null
                  ? organizationLineItemCustomFields.find((x) => x.key === prop.key).name
                  : prop.key
              }}:</b
            >
            {{ prop.value }}
          </span>
        </template>
        <span v-else>{{ DASH }}</span>
      </template>
      <template #budgetFormatted="{ item: lineItem }">
        <span v-if="lineItem.budgetMode === 'I'">{{ DASH }}</span>
        <div
          v-else-if="lineItem.hasInfiniteBudget"
          class="status-icon flex-h">
          <icon-colored-infinity />
          {{ capitalize($t('messages.infiniteBudget')) }}
        </div>
        <span v-else>{{ lineItem.budgetFormatted }}</span>
      </template>
      <template #targetImpressionsFormatted="{ item: lineItem }">
        <span v-if="lineItem.budgetMode === 'B'">{{ DASH }}</span>
        <div
          v-else-if="lineItem.hasInfiniteBudget"
          class="status-icon flex-h">
          <icon-colored-infinity />
          {{ capitalize($t('messages.infiniteBudget')) }}
        </div>
        <span v-else>{{ lineItem.targetImpressionsFormatted }}</span>
      </template>
      <template #minSecondsBetweenPlays="{ item: lineItem }">
        <span v-if="!lineItem.minSecondsBetweenPlays">{{ DASH }}</span>
        <div
          v-else
          class="status-icon flex-h">
          {{ lineItem.frequencyCapFormatted }}
        </div>
      </template>
      <template #createdBy="{ item: lineItem }">
        <UserDetails :user-i-d="lineItem.apiObject.created_by" />
      </template>
      <template #flightRatio="{ item: lineItem }">
        <progress-cell
          v-if="lineItem.flightRatio"
          :use-badge="false"
          :use-progress-bar="true"
          :value="`${lineItem.flightRatio}%`"
          :indicator-class-name="lineItem.hasStarted ? 'normal' : ''" />
        <span v-else>-</span>
      </template>
      <template #campaignType="{ item: lineItem }">
        <span v-if="lineItem.campaign.campaignTypeFormatted">{{ lineItem.campaign.campaignTypeFormatted }}</span>
        <span v-else>-</span>
      </template>
      <template #frequencyCapPerNetwork="{ item: lineItem }">
        <LineItemFrequencyCapPerNetworkCell :line-item="lineItem" />
      </template>
      <template #no-records>
        <no-records-table
          :icon="markRaw(IconComplexLineItem)"
          :header-text="
            isDemandOrganization
              ? $t('messages.tablePlaceholderNoLineItemsDemandSideTitle')
              : $t('messages.tablePlaceholderNoLineItemsSupplySideTitle')
          "
          :details-text="
            isDemandOrganization
              ? noLineItemsMessage || $t('messages.tablePlaceholderNoLineItemsDemandSideDescription')
              : noLineItemsMessage || $t('messages.tablePlaceholderNoLineItemsSupplySideDescription')
          " />
      </template>
      <template #no-match>
        <no-records-table
          :icon="markRaw(IconComplexLineItem)"
          :header-text="$t('messages.tablePlaceholderNoMatchingLineItemsTitle')"
          :details-text="$t('messages.tablePlaceholderNoMatchingItemsDescription')" />
      </template>
    </b-table>
  </div>
</template>

<script type="text/babel">
  import { capitalize, markRaw } from 'vue';
  import { mapActions, mapGetters, mapState } from 'vuex';

  import { Table } from '@/common/components/controls';
  import BulkCreation from '@/common/components/controls/table/BulkCreation.vue';
  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import LineItemFrequencyCapPerNetworkCell from '@/common/components/lineitems/LineItemFrequencyCapPerNetworkCell.vue';
  import ProgressCell from '@/common/components/ui/ProgressCell.vue';
  import { IconColoredInfinity } from '@/common/icons/colored';
  import { IconComplexLineItem2 } from '@/common/icons/complex';
  import { IconErrorFilled, IconGoodFilled, IconWarning, IconWarningFilled } from '@/common/icons/monochrome';
  import { LineItem } from '@/common/models';
  import { DASH, getValueOrDash } from '@/globals';
  import FlightAlertIcon from '@/org-app/components/controls/FlightAlertIcon';
  import MissingCreativeSizeAlertIcon from '@/org-app/components/controls/MissingCreativeSizeAlertIcon';
  import MediaOwners from '@/org-app/components/mediaOwners/MediaOwnersList.vue';
  import UserDetails from '@/org-app/components/users/UserDetails';
  import {
    useAccounts,
    useAdvertisers,
    useMediaOwners,
    useUserOrganizations,
  } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import i18n from '@/vueI18n';

  import LineItemStatusIcon from './LineItemStatusIcon';

  export default {
    name: 'LineItemsTable',
    components: {
      'b-table': Table,
      IconErrorFilled,
      IconGoodFilled,
      IconWarning,
      IconColoredInfinity,
      IconWarningFilled,
      LineItemStatusIcon,
      MediaOwners,
      MissingCreativeSizeAlertIcon,
      FlightAlertIcon,
      ProgressCell,
      UserDetails,
      NoRecordsTable,
      LineItemFrequencyCapPerNetworkCell,
      BulkCreation,
    },
    props: {
      id: {
        type: String,
        default: null,
      },
      lineItemsTotalCount: {
        type: Number,
        default: 0,
      },
      lineItems: {
        type: Array,
        default: () => [],
      },
      showFilters: {
        type: Boolean,
        default: true,
      },
      uiFilters: {
        type: Array,
        default: null,
      },
      fixedFilters: {
        type: Array,
        default: null,
      },
      filtersChangedCallback: {
        type: Function,
        default: null,
      },
      isLoadingLineItems: {
        type: Boolean,
        default: false,
      },
      allowSelection: {
        type: Boolean,
        default: false,
      },
      enableDownload: {
        type: Boolean,
        default: false,
      },
      noLineItemsMessage: {
        type: String,
        default: null,
      },
      castShadow: {
        type: Boolean,
        default: true,
      },
      enableSearchSection: {
        type: Boolean,
        default: true,
      },
      isMonitoringPage: {
        type: Boolean,
        default: false,
      },
      isDvCreativePage: {
        type: Boolean,
        default: false,
      },
      //
      // When a table is flagged as being the main table in a view it will append its filters to the browser's URL.
      //
      isMainTable: {
        type: Boolean,
        default: false,
      },
      isLoadingSelections: {
        type: Boolean,
        default: false,
      },
      getAction: {
        type: Function,
        default: null,
      },
      eventCategory: {
        // Google Analytics purpose
        type: String,
        default: null,
      },
      enableViews: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['selectionChanged', 'download', 'loading-selection'],
    data() {
      return {
        accountsQuery: useAccounts(),
        advertisersQuery: useAdvertisers(),
        mediaOwnersQuery: useMediaOwners(),
        usersQuery: useUserOrganizations(),
        DASH,
        LineItem,
        IconComplexLineItem: IconComplexLineItem2,
      };
    },
    computed: {
      ...mapGetters(['hasChosenOrganization', 'hasChosenExchange', 'membershipSettings']),
      ...mapGetters('orgApp', [
        'isDemandOrganization',
        'isSupplyOrganization',
        'organizationLineItemCustomFields',
        'currentOrganization',
      ]),
      ...mapState('orgApp', {
        organizationSettings: (state) => state.organizationSettings,
      }),
      accounts() {
        return this.accountsQuery?.items ?? [];
      },
      advertisers() {
        return this.advertisersQuery?.items ?? [];
      },
      mediaOwners() {
        return this.mediaOwnersQuery?.items ?? [];
      },
      users() {
        return this.usersQuery?.items ?? [];
      },
      internalFixedFilters() {
        return this.fixedFilters || [];
      },
      tableDownloadColumns() {
        let columns = [
          {
            label: capitalize(i18n.global.t('names.name', { n: 1 })),
            property: 'name',
            type: TableColumnTypes.string,
          },
          {
            label: capitalize(i18n.global.t('names.lineItemID', { n: 1 })),
            property: 'id',
            type: TableColumnTypes.string,
          },
          {
            label: capitalize(i18n.global.t('names.campaignID', { n: 1 })),
            property: 'campaignID',
            type: TableColumnTypes.string,
          },
          {
            label: capitalize(i18n.global.t('names.status', { n: 1 })),
            property: 'status',
            transform: this.capitalize,
            type: TableColumnTypes.string,
          },
          {
            label: capitalize(i18n.global.t('messages.pacingVsExpected')),
            property: 'pacingFormatted',
            type: TableColumnTypes.percentage,
          },
        ];

        if (this.isSupplyOrganization || this.organizationSettings?.uiEnableDemandPriorityManagement) {
          columns = [
            ...columns,
            {
              label: capitalize(i18n.global.t('names.priority', { n: 1 })),
              property: 'priority',
              type: TableColumnTypes.integer,
            },
          ];
        }

        // If it's a demand organization, add the 'pacing vs even' column right after the 'pacing vs expected' columns.
        if (this.isDemandOrganization) {
          columns = [
            ...columns,
            {
              label: capitalize(i18n.global.t('messages.pacingVsEven')),
              property: 'pacingVsEvenFormatted',
              type: TableColumnTypes.percentage,
            },
          ];
        }

        columns = [
          ...columns,
          {
            label: capitalize(i18n.global.t('names.progress', { n: 1 })),
            property: 'progressFormatted',
            type: TableColumnTypes.percentage,
          },
          {
            label: capitalize(i18n.global.t('names.advertiser', { n: 1 })),
            property: 'advertiserDescription',
            type: TableColumnTypes.string,
          },
          {
            label: capitalize(i18n.global.t('names.impressionDelivered', { n: 2 })),
            property: 'totalImpressionsFormatted',
            type: TableColumnTypes.integer,
          },
        ];

        if (this.isSupplyOrganization) {
          columns = [
            ...columns,
            {
              label: capitalize(i18n.global.t('names.adSpend', { n: 1 })),
              property: 'totalSpendOwner',
              type: TableColumnTypes.currency,
            },
            {
              label: capitalize(i18n.global.t('names.cpm', { n: 1 })),
              property: 'averageCPMOwner',
              type: TableColumnTypes.currency,
            },
          ];
        } else {
          columns = [
            ...columns,
            {
              label: capitalize(i18n.global.t('names.adSpend', { n: 1 })),
              property: 'totalSpendBuyer',
              type: TableColumnTypes.currency,
            },
            {
              label: capitalize(i18n.global.t('names.cpm', { n: 1 })),
              property: 'averageCPMBuyer',
              type: TableColumnTypes.currency,
            },
          ];
        }
        columns = [
          ...columns,
          {
            label: capitalize(i18n.global.t('names.purchaseType', { n: 1 })),
            property: 'purchaseTypeName',
            type: TableColumnTypes.string,
          },
          {
            label: capitalize(i18n.global.t('messages.statsLastRefreshedOnUTC')),
            property: 'cachedReportLastUpdatedUtc',
            type: TableColumnTypes.date,
          },
          {
            label: capitalize(i18n.global.t('names.campaign', { n: 1 })),
            property: this.isSupplyOrganization ? 'supplySideCampaignName' : 'campaignName',
            type: TableColumnTypes.string,
          },
          {
            label: capitalize(i18n.global.t('names.account', { n: 1 })),
            property: 'accountName',
            type: TableColumnTypes.string,
          },
        ];

        if (this.isSupplyOrganization) {
          columns = [
            ...columns,
            {
              label: capitalize(i18n.global.t('names.programmaticBuyer', { n: 1 })),
              property: 'organizationNameFormatted',
              type: TableColumnTypes.string,
            },
            {
              label: capitalize(i18n.global.t('names.origin', { n: 1 })),
              property: 'origin',
              type: TableColumnTypes.string,
            },
          ];
        } else if (!this.isDvCreativePage) {
          columns = [
            ...columns,
            {
              label: capitalize(i18n.global.t('names.mediaOwner', { n: 2 })),
              property: 'mediaOwnerIDs',
              transform: (ids) =>
                ids
                  .map((id) => {
                    const mo = this.mediaOwners.find((x) => x.id === id);
                    if (!mo) {
                      return capitalize(i18n.global.t('names.unknown', { n: 1 }));
                    }
                    return mo.name;
                  })
                  .join(','),
              type: TableColumnTypes.string,
            },
          ];
        }

        columns = [
          ...columns,
          {
            label: capitalize(i18n.global.t('names.dealID', { n: 2 })),
            property: 'dealIDFormatted',
            type: TableColumnTypes.string,
          },
          {
            label: capitalize(i18n.global.t('messages.startDateUTC')),
            property: 'startDate',
            type: TableColumnTypes.date,
          },
          {
            label: capitalize(i18n.global.t('messages.endDateUTC')),
            property: 'endDate',
            type: TableColumnTypes.date,
          },
          {
            label: capitalize(i18n.global.t('names.budget', { n: 1 })),
            property: 'cpmCalculator',
            transform: (cpmCalculator) => cpmCalculator.budget,
            type: TableColumnTypes.currency,
          },
          {
            label: capitalize(i18n.global.t('messages.impressionGoal')),
            property: 'cpmCalculator',
            transform: (cpmCalculator) => cpmCalculator.targetImpressions,
            type: TableColumnTypes.integer,
          },
          {
            label: capitalize(i18n.global.t('messages.lastModifiedOnUTC')),
            property: 'modifiedOnUtc',
            type: TableColumnTypes.datetime,
          },
          {
            label: capitalize(i18n.global.t('messages.lastPlayedOnUTC')),
            property: 'lastPlayedTime',
            type: TableColumnTypes.datetime,
          },
          {
            label: this.capitalize(i18n.global.t('names.frequencyCap', { n: 1 })),
            property: 'minSecondsBetweenPlays',
            type: TableColumnTypes.integer,
          },
        ];

        if (this.isSupplyOrganization) {
          columns = [
            ...columns,
            {
              label: this.capitalize(i18n.global.t('messages.frequencyCapPerNetwork')),
              property: 'frequencyCapPerNetwork',
              type: TableColumnTypes.string,
            },
          ];
        }

        columns = [
          ...columns,
          {
            label: capitalize(i18n.global.t('names.organizationCustomField', { n: 2 })),
            property: (li) => {
              const customFields = li.lineItemProperties.filter(
                (x) => this.organizationLineItemCustomFields.find((y) => y.key === x.key) != null,
              );
              if (this.organizationLineItemCustomFields == null || customFields.length === 0) {
                return null;
              }
              const getName = (cf) =>
                this.organizationLineItemCustomFields.find((x) => x.key === cf.key) != null
                  ? this.organizationLineItemCustomFields.find((x) => x.key === cf.key).name
                  : cf.key;
              return customFields.map((cf) => `[${getName(cf)}]: ${cf.value}`).join(', ');
            },
            type: TableColumnTypes.string,
          },
        ];

        if (this.membershipSettings.uiEnableFlights) {
          columns.push({
            label: capitalize(i18n.global.t('names.flightGoalRatio', { n: 1 })),
            property: 'flightRatio',
            type: TableColumnTypes.integer,
            transform: (value) => `${value}%`,
          });
        }

        columns.push(
          {
            label: capitalize(i18n.global.t('names.campaignType', { n: 1 })),
            property: 'campaignType',
            type: TableColumnTypes.string,
          },
          {
            label: capitalize(i18n.global.t('messages.createdBy')),
            property: 'createdByUserID',
            type: TableColumnTypes.string,
            transform: (userID) => getValueOrDash(this.users.find((u) => u.userID === userID)?.email),
          },
        );

        return columns;
      },
      columns() {
        const columns = {};

        columns.link = LineItem.entityProperties.name.getTableColumn();
        columns.campaignID = LineItem.entityProperties.campaignID.getTableColumn();
        columns.id = LineItem.entityProperties.id.getTableColumn();
        if (this.organizationSettings?.enableDisplayVideoChannel) {
          columns.channelType = LineItem.entityProperties.channelType.getTableColumn();
        }
        columns.statusDescription = LineItem.entityProperties.status.getTableColumn();
        columns.pacingFormatted = LineItem.entityProperties.pacing.getTableColumn();
        if (this.isDemandOrganization) {
          columns.pacingVsEvenFormatted = TableColumn.fromObject({
            label: capitalize(i18n.global.t('messages.pacingVsEven', { n: 1 })),
          });
        }
        columns.progressFormatted = LineItem.entityProperties.progress.getTableColumn();
        columns.fullPacingStrategyName = LineItem.entityProperties.deliveryCurve.getTableColumn({
          isEditableHandler: (lineItem) => lineItem?.isChannelTypeDOOH,
        });
        if (this.isSupplyOrganization || this.organizationSettings?.uiEnableDemandPriorityManagement) {
          columns.reversedPriority = LineItem.entityProperties.reversedPriority.getTableColumn({
            isEditableHandler: (lineItem) => lineItem?.isChannelTypeDOOH,
          });
        }
        columns.advertiserDescription = LineItem.entityProperties.advertiser.getTableColumn();
        columns.totalImpressionsFormatted = LineItem.entityProperties.totalImpressions.getTableColumn();
        if (this.isSupplyOrganization) {
          columns.totalSpendOwnerFormatted = LineItem.entityProperties.totalSpendOwner.getTableColumn();
          columns.averageCPMOwnerFormatted = LineItem.entityProperties.averageCPMOwner.getTableColumn();
        } else {
          columns.totalSpendBuyerFormatted = LineItem.entityProperties.totalSpendBuyer.getTableColumn();
          columns.averageCPMBuyerFormatted = LineItem.entityProperties.averageCPMBuyer.getTableColumn();
        }
        /**
         * Commenting the following code block because of some huge loading time issues happening in the monitoring page or any page
         * that has the LI table. Will eventually uncomment it once were sure that the cadence related problem is over.
         */
        // if (this.isSupplyOrganization) {
        //   columns.cadenceFormatted = LineItem.entityProperties.cadence.getTableColumn();
        //   columns.actualCadenceFormatted = TableColumn.fromObject({
        //     label: this.capitalize(i18n.global.t('messages.averageActualCadence')),
        //     property: 'average_actual_cadence',
        //     type: TableColumnTypes.float,
        //     allowSort: false,
        //   });
        //   columns.scheduledCadenceFormatted = TableColumn.fromObject({
        //     label: this.capitalize(i18n.global.t('messages.averageScheduledCadence')),
        //     property: 'average_scheduled_cadence',
        //     type: TableColumnTypes.float,
        //     allowSort: false,
        //   });
        // }
        columns.purchaseTypeName = LineItem.entityProperties.purchaseType.getTableColumn({
          allowSort: false,
        });
        columns.cachedReportLastUpdatedOnFormatted =
          LineItem.entityProperties.cachedReportLastUpdatedOn.getTableColumn();
        if (this.isSupplyOrganization) {
          columns.supplySideCampaignName = LineItem.entityProperties.campaignName.getTableColumn();
        } else {
          columns.campaignName = LineItem.entityProperties.campaignName.getTableColumn();
        }
        columns.accountName = LineItem.entityProperties.account.getTableColumn({
          postMutationCallback: (value, entity) => {
            //
            // There could be multiple line items with the same campaign in the table, update them all.
            //
            this.lineItems
              .filter((x) => x.campaignID === entity.campaignID)
              .forEach((x) => {
                x.campaign.account = value;
              });
          },
        });
        if (this.isDemandOrganization && !this.isDvCreativePage) {
          columns.mediaOwnerIDs = LineItem.entityProperties.mediaOwners.getTableColumn({
            allowSort: false,
            useAsFilter: !this.isMonitoringPage,
            isEditableHandler: (lineItem) => lineItem?.isChannelTypeDOOH,
          });
        } else {
          columns.organizationNameFormatted = TableColumn.fromObject({
            label: capitalize(i18n.global.t('names.programmaticBuyer', { n: 1 })),
          });
          columns.origin = LineItem.entityProperties.origin.getTableColumn({
            orderBy: ['creation_origin', 'package_id'],
          });
        }
        columns.minSecondsBetweenPlays = LineItem.entityProperties.minSecondsBetweenPlays.getTableColumn();
        if (this.isSupplyOrganization) {
          columns.frequencyCapPerNetwork = TableColumn.fromObject({
            label: this.capitalize(i18n.global.t('messages.frequencyCapPerNetwork')),
            property: 'frequencyCapPerNetwork',
            type: TableColumnTypes.string,
            allowSort: false,
          });
        }
        columns.dealIDFormatted = TableColumn.fromObject({
          label: capitalize(i18n.global.t('names.dealID', { n: 2 })),
        });
        columns.startDateFormatted = LineItem.entityProperties.startDate.getTableColumn();
        columns.endDateFormatted = LineItem.entityProperties.endDate.getTableColumn();
        columns.budgetFormatted = LineItem.entityProperties.budget.getTableColumn();
        columns.targetImpressionsFormatted = LineItem.entityProperties.targetImpressions.getTableColumn();
        columns.lineItemProperties = TableColumn.fromObject({
          label: capitalize(i18n.global.t('names.organizationCustomField', { n: 2 })),
        });
        columns.modifiedOnFormatted = LineItem.entityProperties.modifiedOn.getTableColumn();
        columns.lastPlayedTimeFormatted = TableColumn.fromObject({
          label: capitalize(i18n.global.t('messages.lastPlayedOn')),
        });
        columns.createdBy = LineItem.entityProperties.createdBy.getTableColumn({
          allowSort: false,
          isDefault: true,
        });

        if (this.membershipSettings.uiEnableFlights) {
          columns.flightRatio = LineItem.entityProperties.flightRatio.getTableColumn();
        }
        columns.campaignType = LineItem.entityProperties.campaignType.getTableColumn();

        return columns;
      },
      isWhiteLabeledOrganization() {
        return this.currentOrganization.exchange?.isWhiteLabeled;
      },
    },
    async mounted() {
      await waitForPendingQueries([this.advertisersQuery, this.accountsQuery, this.mediaOwnersQuery, this.usersQuery]);

      if (this.hasChosenOrganization) {
        this.getOrganizationCustomFields({ showLoader: false });
      }
    },
    methods: {
      useAdvertisers,
      useAccounts,
      markRaw,
      ...mapActions('orgApp', ['getOrganizationCustomFields', 'saveLineItem']),
      capitalize,
      getReferenceToTable() {
        return this.$refs.table;
      },
      filtersChanged(queryParams) {
        if (this.filtersChangedCallback != null) {
          this.filtersChangedCallback(queryParams);
        }
      },
      onDownload(cb) {
        const { queryParams } = this.getReferenceToTable();
        this.$emit('download', {
          callback: cb,
          queryParams,
        });
      },
      onSelectionChanged(e) {
        this.$emit('selectionChanged', e);
      },
    },
  };
</script>
